import {PlacesServiceContext} from 'context/PlacesServiceContext';
import usePrevious from 'hooks/usePrevious';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {Station} from 'lib/api/backend.schemas';

import {isWebBrowser} from 'utils/maps/loadMaps';

import {ConsentContext} from 'components/section-components/ConsentManager';

import MapsConsentOverlay from '../MapsConsentOverlay';
import {
  IMarkersObject,
  changeMarkersAccordingToFilter,
  changeMarkersAccordingToSelectedStationAndCity,
} from './MapFunctions';
import styles from './StarcarMap.module.scss';
import stationIcon from './icon_map_station.png';

export const StarcarMap = ({
  stations = [],
  onClickMarker,
  activeStation,
  cityStations = [],
  filteredDataIds,
  height = 500,
  width = 600,
}: {
  stations: Station[];
  onClickMarker: (id: string) => void;
  activeStation: string | boolean;
  cityStations?: string[];
  filteredDataIds?: string[];
  height?: number;
  width?: number;
}) => {
  const {initialized, init: initMaps} = useContext(PlacesServiceContext);
  const [_headerHeight, setHeaderHeight] = useState(91);
  useEffect(() => {
    if (isWebBrowser) {
      setHeaderHeight(document?.getElementById('headerWrapper')?.offsetHeight);
    }
  }, []);
  const {
    state: {maps},
  } = useContext(ConsentContext);

  const [isVisible, setIsVisible] = useState(false);
  const mapRef = useRef();

  const map = useRef<google.maps.Map>();
  const [markers, setMarkers] = useState<IMarkersObject>({} as IMarkersObject);
  const init = () => {
    const initBounds = new google.maps.LatLngBounds();
    stations.forEach(station => {
      initBounds.extend(new google.maps.LatLng(station.latitude, station.longitude));
    });
    map.current = new google.maps.Map(mapRef.current, {
      zoom: 15,
      zoomControl: true,
      center: initBounds.getCenter(),
      streetViewControl: true,
      scrollwheel: false,
    });
    if (stations.length > 1) {
      map.current.fitBounds(initBounds, 1);
    }
    const zoom = Math.min(map.current.getZoom(), 15);
    map.current.setZoom(zoom);
    const markers = stations.reduce((acc, station) => {
      acc[station.id] = createMarker(station);
      return acc;
    }, {}) as IMarkersObject;
    setMarkers(markers);
  };

  const createMarker = useCallback(
    (station: Station) => {
      var marker = new google.maps.Marker({
        map: map.current,
        position: new google.maps.LatLng(station.latitude, station.longitude),
        icon: {
          url: stationIcon.src,
          scaledSize: new google.maps.Size(52.5, 67.5),
        },
      });

      marker.addListener('click', function () {
        // closeInfoboxes();
        if (onClickMarker) {
          onClickMarker(station.id);
        }
      });
      return marker;
    },
    [map, onClickMarker],
  );
  useEffect(() => {
    if (maps) {
      initMaps().then(() => {
        setIsVisible(true);
      });
    }
    if (mapRef.current && isVisible) {
      initMaps().then(() => init());
    }
  }, [mapRef.current, initialized, maps, isVisible]);
  useEffect(() => {
    if (filteredDataIds?.length) {
      changeMarkersAccordingToFilter({activeStation, cityStations, filteredDataIds, markers});
    }
  }, [filteredDataIds]);
  const lastActiveStation = usePrevious(activeStation);
  const lastActiveCitystations = usePrevious(cityStations);

  useEffect(() => {
    changeMarkersAccordingToSelectedStationAndCity({
      activeStation,
      lastActiveStation,
      lastActiveCitystations,
      markers,
      cityStations,
      filteredDataIds,
    });
  }, [
    activeStation,
    markers,
    lastActiveStation,
    isVisible,
    cityStations,
    lastActiveCitystations,
    filteredDataIds,
  ]);
  return (
    <div>
      <div className={isVisible ? styles.relative : styles.absolute}>
        <div style={{height: height}} ref={mapRef}></div>
      </div>
      {!isVisible ? (
        <MapsConsentOverlay
          height={height}
          width={width}
          onSetVisible={value => initMaps().then(() => setIsVisible(value))}
        />
      ) : null}
    </div>
  );
};
